/**
 * Which Azure cloud this build signs in to, and the hosts that differ.
 *
 * The commercial cloud and Azure Government (which hosts GCC High tenants) have
 * separate sign-in and Microsoft Graph endpoints. A build says which one it is
 * for with `VITE_AZURE_CLOUD` (`public`, the default, or `usgov`), mirroring the
 * server's `AZURE_CLOUD` (web/api/_azureCloud.js). Either host can be set on its
 * own for a cloud this table does not name: `VITE_ENTRA_AUTHORITY_HOST`,
 * `VITE_GRAPH_BASE_URL`.
 */

export interface AzureCloudHosts {
  name: 'public' | 'usgov'
  /** Host of the Entra sign-in authority, no scheme. */
  authorityHost: string
  /** Microsoft Graph origin, no trailing slash. */
  graphBaseUrl: string
}

const USGOV_NAMES = new Set(['usgov', 'usgovernment', 'azureusgovernment', 'gov', 'government', 'gcch', 'gcc-high', 'gcchigh'])

function clean(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

/** PURE: resolves the hosts from the settings passed in. */
export function resolveAzureCloud(settings: { cloud?: unknown; authorityHost?: unknown; graphBaseUrl?: unknown }): AzureCloudHosts {
  const gov = USGOV_NAMES.has(clean(settings.cloud).toLowerCase())
  const host = clean(settings.authorityHost).replace(/^https?:\/\//, '').replace(/\/+$/, '')
  return {
    name: gov ? 'usgov' : 'public',
    authorityHost: host || (gov ? 'login.microsoftonline.us' : 'login.microsoftonline.com'),
    graphBaseUrl: (clean(settings.graphBaseUrl) || (gov ? 'https://graph.microsoft.us' : 'https://graph.microsoft.com')).replace(/\/+$/, ''),
  }
}

/** The hosts for this build. */
export function azureCloud(): AzureCloudHosts {
  return resolveAzureCloud({
    cloud: import.meta.env.VITE_AZURE_CLOUD,
    authorityHost: import.meta.env.VITE_ENTRA_AUTHORITY_HOST,
    graphBaseUrl: import.meta.env.VITE_GRAPH_BASE_URL,
  })
}

/** The Entra authority URL for a tenant in this build's cloud. */
export function entraAuthority(tenant: string): string {
  return `https://${azureCloud().authorityHost}/${tenant}`
}
